import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ViewDidLeave, ViewWillEnter } from '@ionic/angular';
import { interval, startWith, Subscription } from 'rxjs';
import { LoggingService } from 'src/app/services/logging.service';
import { wait } from 'src/app/utils/async';
import { toNumber } from 'src/app/utils/number';

@Component({
  selector: 'app-promotion-content',
  templateUrl: './promotion-content.component.html',
  styleUrls: ['./promotion-content.component.scss'],
  standalone: true,
  imports: []
})
export class PromotionContentComponent implements OnInit, ViewWillEnter, ViewDidLeave {
  loggingService = inject(LoggingService);

  slides: Slide[] = [
    { src: '/assets/images/stay-by-us-screenshot.webp' },
    { src: '/assets/images/femcare-screenshot.webp' },
    { src: '/assets/images/monsey-shabbos-rental-screenshot.webp' }
  ];

  currentSlideIndex = signal(0);
  nextSlideIndex = computed(() => this.getNextIndex());
  nextSlide = computed(() => this.slides[this.nextSlideIndex()]);

  slide1 = signal<Slide | undefined>(undefined);
  slide2 = signal<Slide | undefined>(undefined);

  subscription = new Subscription();
  initialized = signal(false);
  timer$ = interval(12_000).pipe(startWith(null));

  ngOnInit() {
    this.init();
  }

  ionViewWillEnter() {
    this.init();
  }

  ionViewDidLeave() {
    this.dispose();
  }

  private init() {
    if (this.initialized()) return;
    if (this.subscription.closed) {
      this.subscription = new Subscription();
    }
    const slideSubscription = this.timer$.subscribe(() => this.fadeToNextSlide());
    this.subscription.add(slideSubscription);
    this.initialized.set(true);
  }

  private dispose() {
    this.subscription.unsubscribe();
    this.initialized.set(false);
  }

  getNextIndex() {
    return (toNumber(this.currentSlideIndex()) + 1) % this.slides.length;
  }

  private async fadeToNextSlide() {
    const slides = this.slides;
    if (!slides || slides.length === 1) return;
    const nextSlideIndex = this.nextSlideIndex();

    this.currentSlideIndex.set(nextSlideIndex);
    this.transition();
  }

  isEvenIndex() {
    return this.currentSlideIndex() % 2 === 0;
  }

  async transition() {
    const nextSlide = this.nextSlide();
    let slideOutPhaser;
    if (this.slide1()) {
      this.slide2.set(nextSlide);
      slideOutPhaser = () => this.slide1.set(undefined);
    } else {
      this.slide1.set(nextSlide);
      slideOutPhaser = () => this.slide2.set(undefined);
    }
    await wait(2_000);
    slideOutPhaser();
  }
}

export interface Slide {
  src: string;
}